import type { JSX } from 'react';
import type {
  ContactResource,
  ContactRoleValue,
  CreateContactBody,
  UpdateContactBody,
} from '@voxler/contracts';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { crmSend, errorMessage, ROLE_HINT, ROLE_LABEL, ROLES } from '../../lib/crm';
import { MutationError, useCrmRefresh } from './shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHead,
} from '@/components/ui/dialog';
import { CheckRow, Field, FieldHint, Fields2, Fieldset } from '@/components/ui/field';
import { Input } from '@/components/ui/input';

export type ContactDialogMode =
  | {
      kind: 'create';
      companyId: string;
      companyName: string;
      /** Current primary contact, named in the « make primary » option. */
      primaryName: string | null;
    }
  | { kind: 'edit'; contact: ContactResource };

const nullable = (value: string): string | null => (value.trim() === '' ? null : value.trim());

/** « Nouveau contact » / « Modifier le contact » (ContactDialog.dc.html). */
export function ContactDialog({
  mode,
  onClose,
  onCreated,
}: {
  mode: ContactDialogMode | null;
  onClose: () => void;
  /** Called with the created contact (the deal page links it to the deal). */
  onCreated?: (contact: ContactResource) => void;
}): JSX.Element {
  return (
    <Dialog
      open={mode !== null}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      {mode !== null ? (
        <ContactForm mode={mode} onClose={onClose} {...(onCreated ? { onCreated } : {})} />
      ) : null}
    </Dialog>
  );
}

function ContactForm({
  mode,
  onClose,
  onCreated,
}: {
  mode: ContactDialogMode;
  onClose: () => void;
  onCreated?: (contact: ContactResource) => void;
}): JSX.Element {
  const refresh = useCrmRefresh();
  const editing = mode.kind === 'edit' ? mode.contact : null;
  const [values, setValues] = useState({
    firstName: editing?.firstName ?? '',
    lastName: editing?.lastName ?? '',
    jobTitle: editing?.jobTitle ?? '',
    email: editing?.email ?? '',
    phone: editing?.phone ?? '',
  });
  const [roles, setRoles] = useState<readonly ContactRoleValue[]>(editing?.roles ?? []);
  const [makePrimary, setMakePrimary] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const errors = {
    firstName: values.firstName.trim() === '' ? 'Indiquer le prénom.' : undefined,
    lastName: values.lastName.trim() === '' ? 'Indiquer le nom.' : undefined,
  };
  const valid = errors.firstName === undefined && errors.lastName === undefined;
  const shown = (error: string | undefined): string | undefined => (submitted ? error : undefined);

  const save = useMutation({
    mutationFn: () => {
      const fields = {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        jobTitle: nullable(values.jobTitle),
        email: nullable(values.email),
        phone: nullable(values.phone),
        roles: [...roles],
      };
      if (mode.kind === 'edit') {
        const body: UpdateContactBody = fields;
        return crmSend<ContactResource>('PATCH', `/contacts/${mode.contact.id}`, body);
      }
      const body: CreateContactBody = { ...fields, makePrimary };
      return crmSend<ContactResource>('POST', `/companies/${mode.companyId}/contacts`, body);
    },
    onSuccess: (contact) => {
      refresh();
      if (mode.kind === 'create') {
        onCreated?.(contact);
      }
      onClose();
    },
  });

  const toggleRole = (role: ContactRoleValue, on: boolean): void => {
    setRoles((current) =>
      on
        ? ROLES.filter((r) => r === role || current.includes(r))
        : current.filter((r) => r !== role),
    );
  };

  return (
    <DialogContent>
      <DialogHead
        title={editing !== null ? 'Modifier le contact' : 'Nouveau contact'}
        description={
          mode.kind === 'create'
            ? `Ajouter un contact chez ${mode.companyName}.`
            : `${mode.contact.firstName} ${mode.contact.lastName}`
        }
      />
      <form
        id="contact-form"
        className="contents"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);
          if (valid) {
            save.mutate();
          }
        }}
      >
        <DialogBody>
          <Fieldset title="Identité">
            <Fields2>
              <Field label="Prénom" required error={shown(errors.firstName)}>
                <Input
                  value={values.firstName}
                  maxLength={100}
                  onChange={(event) => {
                    setValues({ ...values, firstName: event.target.value });
                  }}
                />
              </Field>
              <Field label="Nom" required error={shown(errors.lastName)}>
                <Input
                  value={values.lastName}
                  maxLength={100}
                  onChange={(event) => {
                    setValues({ ...values, lastName: event.target.value });
                  }}
                />
              </Field>
            </Fields2>
            <Field label="Fonction">
              <Input
                value={values.jobTitle}
                maxLength={200}
                onChange={(event) => {
                  setValues({ ...values, jobTitle: event.target.value });
                }}
              />
            </Field>
            <Fields2>
              <Field label="Email">
                <Input
                  type="email"
                  value={values.email}
                  maxLength={200}
                  onChange={(event) => {
                    setValues({ ...values, email: event.target.value });
                  }}
                />
              </Field>
              <Field label="Téléphone">
                <Input
                  type="tel"
                  value={values.phone}
                  maxLength={40}
                  onChange={(event) => {
                    setValues({ ...values, phone: event.target.value });
                  }}
                />
              </Field>
            </Fields2>
          </Fieldset>

          <Fieldset title="Rôles">
            <div className="grid gap-2">
              {ROLES.map((role) => (
                <CheckRow
                  key={role}
                  label={
                    <>
                      {ROLE_LABEL[role]}{' '}
                      <small className="text-xs text-muted-foreground">{ROLE_HINT[role]}</small>
                    </>
                  }
                  checked={roles.includes(role)}
                  onChange={(event) => {
                    toggleRole(role, event.target.checked);
                  }}
                />
              ))}
            </div>
            <FieldHint>
              Le contact Facturation fournit l’email de facturation lors du passage en client.
            </FieldHint>
          </Fieldset>

          {mode.kind === 'create' ? (
            <CheckRow
              label={
                mode.primaryName !== null
                  ? `Définir comme contact principal, à la place de ${mode.primaryName}`
                  : 'Définir comme contact principal'
              }
              checked={makePrimary}
              onChange={(event) => {
                setMakePrimary(event.target.checked);
              }}
            />
          ) : null}
          <MutationError
            message={save.isError ? errorMessage(save.error, 'Échec de l’enregistrement.') : null}
          />
        </DialogBody>
      </form>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Annuler
        </Button>
        <Button type="submit" form="contact-form" size="lg" disabled={save.isPending}>
          {editing !== null ? 'Enregistrer' : 'Ajouter le contact'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
